import { events } from '@/app/events/events';
import { getIsEmbedMode } from '@/app/helpers/sharedArrayBufferSupport';
import { snackbar } from '@/app/gridGL/pixiApp/gridSettings';
import { financialDataRateLimitMessage } from '@/shared/utils/financialRateLimitHttp';
import type { LanguageState } from '@/app/web-workers/languageTypes';
import type {
  ClientPythonMessage,
  PythonClientCaptureChartImage,
  PythonClientFinancialData,
  PythonClientGetJwt,
  PythonClientGetTeamUuid,
  PythonClientMessage,
} from '@/app/web-workers/pythonWebWorker/pythonClientMessages';
import { quadraticCore } from '@/app/web-workers/quadraticCore/quadraticCore';
import { authClient } from '@/auth/auth';
import { connectionClient } from '@/shared/api/connectionClient';
import { trackEvent } from '@/shared/utils/analyticsEvents';
import {
  createBrowserInducedAbortError,
  formatWorkerErrorMessage,
  isLikelyBrowserAbort,
} from '@/shared/utils/browserLifecycle';

const CHART_IMAGE_TIMEOUT_MS = 10000;
const EXPECTED_CHART_CAPTURE_ERRORS = new Set(['No content window', 'Plotly not available', 'No Plotly element found']);

// Plotly's `toImage` rejects with a DOM `Event` (not an `Error`) when the
// underlying SVG-to-PNG image fails to load (tainted canvas, malformed SVG,
// etc.). These are non-actionable, environment-specific failures. Treat that
// specific DOM `Event` rejection — alongside our own known guard messages — as
// expected and log it at `warn` to avoid Sentry noise (it only captures
// `console.error`). Any other unexpected rejection shape (null, string, plain
// object, or an Error with an unknown message) still surfaces as an error.
const isExpectedChartCaptureError = (error: unknown) =>
  (typeof Event !== 'undefined' && error instanceof Event) ||
  (error instanceof Error && EXPECTED_CHART_CAPTURE_ERRORS.has(error.message));

const showFinancialRateLimitSnackbar = (retryAfterSeconds: number | undefined) => {
  const seconds = retryAfterSeconds && retryAfterSeconds > 0 ? retryAfterSeconds : 60;
  snackbar(financialDataRateLimitMessage(seconds), { severity: 'warning' });
};

class PythonWebWorker {
  state: LanguageState = 'loading';

  private worker?: Worker;
  private initPromise?: Promise<void>;
  private initReject?: (error: Error) => void;

  private send(message: ClientPythonMessage, port?: MessagePort) {
    if (!this.worker) throw new Error('Expected worker to be defined in python.ts');
    if (port) {
      this.worker.postMessage(message, [port]);
    } else {
      this.worker.postMessage(message);
    }
  }

  private handleMessage = (message: MessageEvent<PythonClientMessage>) => {
    switch (message.data.type) {
      case 'pythonClientInit':
        this.state = 'ready';
        events.emit('pythonInit', message.data.version);
        this.send({ type: 'clientPythonInit', env: import.meta.env });
        break;

      case 'pythonClientState':
        this.state = message.data.state;
        break;

      case 'pythonClientGetJwt':
        authClient.getTokenOrRedirect(true).then((jwt) => {
          const data = message.data as PythonClientGetJwt;
          this.send({ type: 'clientPythonGetJwt', id: data.id, jwt });
        });
        break;

      case 'pythonClientGetTeamUuid':
        {
          const data = message.data as PythonClientGetTeamUuid;
          const teamUuid = quadraticCore.getTeamUuid();
          this.send({ type: 'clientPythonGetTeamUuid', id: data.id, teamUuid });
        }
        break;

      case 'pythonClientFinancialData':
        {
          const data = message.data as PythonClientFinancialData;

          trackEvent('[PythonWebWorker].qFinancialRequest', { endpoint: data.endpoint });

          // teamUuid is sent through as the X-Team-Id header. For anonymous
          // viewers of public files it is the file's owning team; for embed
          // mode it's a randomly-generated UUID (the real team is hidden).
          // Either is acceptable since /financial does not enforce team auth.
          const teamUuid = quadraticCore.getTeamUuid() ?? '';

          connectionClient.financial
            .request(teamUuid, data.endpoint, JSON.parse(data.paramsJson))
            .then((result) => {
              if (result.rateLimited) {
                showFinancialRateLimitSnackbar(result.retryAfterSeconds);
              }
              this.send({
                type: 'clientPythonFinancialData',
                id: data.id,
                data: result.data,
                error: result.error,
              });
            })
            .catch((error) => {
              this.send({
                type: 'clientPythonFinancialData',
                id: data.id,
                data: null,
                error: error.message || 'Unknown error fetching financial data',
              });
            });
        }
        break;

      case 'pythonClientCaptureChartImage':
        this.captureChartImage(message.data as PythonClientCaptureChartImage);
        break;

      default:
        throw new Error(`Unhandled message type ${message.type}`);
    }
  };

  private captureChartImage = async (data: PythonClientCaptureChartImage) => {
    const { id, html, width, height } = data;
    let image: string | null = null;

    try {
      image = await this.renderChartToImage(html, width, height);
    } catch (e) {
      console.error('[pythonWebWorker] Failed to capture chart image:', e);
    }

    this.send({ type: 'clientPythonChartImage', id, image });
  };

  private renderChartToImage = (html: string, width: number, height: number): Promise<string | null> => {
    return new Promise((resolve) => {
      const startTime = Date.now();
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.left = '-9999px';
      iframe.style.top = '-9999px';
      iframe.style.width = `${width}px`;
      iframe.style.height = `${height}px`;
      iframe.style.border = 'none';
      iframe.style.visibility = 'hidden';

      let resolved = false;
      const cleanup = () => {
        if (iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
      };

      const timeoutId = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          cleanup();
          resolve(null);
        }
      }, CHART_IMAGE_TIMEOUT_MS);

      iframe.onload = async () => {
        try {
          const contentWindow = iframe.contentWindow;
          if (!contentWindow) {
            throw new Error('No content window');
          }

          const remainingTimeout = Math.max(0, CHART_IMAGE_TIMEOUT_MS - (Date.now() - startTime));
          const plotly = await this.waitForPlotly(contentWindow, remainingTimeout);
          if (!plotly) {
            throw new Error('Plotly not available');
          }

          const plotElement = contentWindow.document.querySelector('.js-plotly-plot');
          if (!plotElement) {
            throw new Error('No Plotly element found');
          }

          // Wait for chart to finish rendering before capturing
          await this.waitForPlotlyRender(plotElement);

          // Resize the chart to match the target dimensions before capturing
          await plotly.relayout(plotElement, { width, height, autosize: false });

          // Wait for the resize to complete
          await this.waitForPlotlyRender(plotElement);

          // Safari's canvas.toDataURL('image/webp') silently falls back to PNG,
          // so request PNG explicitly to keep MIME types accurate cross-browser.
          const dataUrl = await plotly.toImage(plotElement, {
            format: 'png',
            width,
            height,
          });

          if (!resolved) {
            resolved = true;
            clearTimeout(timeoutId);
            cleanup();
            resolve(dataUrl);
          }
        } catch (e) {
          if (isExpectedChartCaptureError(e)) {
            console.warn('[pythonWebWorker] Chart image capture skipped:', e);
          } else {
            console.error('[pythonWebWorker] Error capturing chart:', e);
          }
          if (!resolved) {
            resolved = true;
            clearTimeout(timeoutId);
            cleanup();
            resolve(null);
          }
        }
      };

      iframe.onerror = (e) => {
        console.error('[pythonWebWorker] Iframe failed to load:', e);
        if (!resolved) {
          resolved = true;
          clearTimeout(timeoutId);
          cleanup();
          resolve(null);
        }
      };

      iframe.srcdoc = html;
      document.body.appendChild(iframe);
    });
  };

  private waitForPlotly = (
    contentWindow: Window,
    timeoutMs: number
  ): Promise<{
    toImage: (el: Element, opts: object) => Promise<string>;
    relayout: (el: Element, update: object) => Promise<void>;
  } | null> => {
    return new Promise((resolve) => {
      const startTime = Date.now();

      const check = () => {
        const plotly = (contentWindow as any).Plotly;
        if (plotly && typeof plotly.toImage === 'function' && typeof plotly.relayout === 'function') {
          resolve(plotly);
          return;
        }

        if (Date.now() - startTime > timeoutMs) {
          resolve(null);
          return;
        }

        setTimeout(check, 50);
      };

      check();
    });
  };

  // Wait for Plotly chart to finish rendering (fires plotly_afterplot event)
  private waitForPlotlyRender = (plotElement: Element): Promise<void> => {
    return new Promise((resolve) => {
      // Check if chart already has rendered data (gd._fullData exists and has content)
      const gd = plotElement as any;
      if (gd._fullData && gd._fullData.length > 0 && gd._fullLayout) {
        // Chart appears to be fully rendered
        resolve();
        return;
      }

      // Otherwise wait for the afterplot event with a fallback timeout
      const RENDER_TIMEOUT_MS = 5000;
      let resolved = false;

      const onAfterPlot = () => {
        if (!resolved) {
          resolved = true;
          plotElement.removeEventListener('plotly_afterplot', onAfterPlot);
          resolve();
        }
      };

      plotElement.addEventListener('plotly_afterplot', onAfterPlot);

      // Fallback timeout in case the event doesn't fire
      setTimeout(() => {
        if (!resolved) {
          resolved = true;
          plotElement.removeEventListener('plotly_afterplot', onAfterPlot);
          console.warn(
            '[pythonWebWorker] Plotly render timeout hit after',
            RENDER_TIMEOUT_MS,
            'ms — chart may be incomplete or have a large dataset'
          );
          resolve();
        }
      }, RENDER_TIMEOUT_MS);
    });
  };

  initWorker() {
    this.worker?.terminate();
    try {
      this.worker = new Worker(new URL('./worker/python.worker.ts', import.meta.url), { type: 'module' });
      this.worker.onmessage = this.handleMessage;
      this.worker.onerror = (error) => {
        const formatted = formatWorkerErrorMessage(error, 'Python worker error');
        const browserAbort = isLikelyBrowserAbort(error);
        if (browserAbort) {
          console.warn(`[pythonWebWorker] worker.onerror (suppressed, browser-induced abort): ${formatted}`, error);
        } else if (!error.message) {
          // Module worker load failures arrive as a contentless `ErrorEvent`
          // (no message/filename/line/col) — Sentry only sees "[object Event]"
          // with no actionable root cause. Keep these as warnings, matching
          // javascriptWebWorker/quadraticCore/renderWebWorker.
          console.warn(`[pythonWebWorker] worker.onerror (no details available): ${formatted}`, error);
        } else {
          console.error('[pythonWebWorker] Worker error:', error);
        }
        if (this.initReject) {
          const errorObj = browserAbort
            ? createBrowserInducedAbortError(formatted)
            : error.error instanceof Error
              ? error.error
              : new Error(formatted);
          this.initReject(errorObj);
          this.initReject = undefined;
        }
      };

      const pythonCoreChannel = new MessageChannel();
      this.send({ type: 'clientPythonCoreChannel', isEmbedMode: getIsEmbedMode() }, pythonCoreChannel.port1);
      quadraticCore.sendPythonInit(pythonCoreChannel.port2);
    } catch (error) {
      if (this.initReject) {
        this.initReject(error instanceof Error ? error : new Error(String(error)));
        this.initReject = undefined;
      }
      throw error;
    }
  }

  isInitialized(): boolean {
    return this.worker !== undefined;
  }

  async ensureInitialized(): Promise<void> {
    if (this.worker && this.state === 'ready') {
      return;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    // Emit loading event when initialization starts
    events.emit('pythonLoading');

    this.initPromise = new Promise<void>((resolve, reject) => {
      this.initReject = reject;

      const timeout = setTimeout(() => {
        events.off('pythonInit', onInit);
        this.initPromise = undefined;
        this.initReject = undefined;
        reject(new Error('Python worker initialization timed out'));
      }, 30000);

      const onInit = () => {
        clearTimeout(timeout);
        events.off('pythonInit', onInit);
        this.initPromise = undefined;
        this.initReject = undefined;
        resolve();
      };

      events.on('pythonInit', onInit);

      try {
        this.initWorker();
      } catch (error) {
        clearTimeout(timeout);
        events.off('pythonInit', onInit);
        this.initPromise = undefined;
        this.initReject = undefined;
        reject(error);
      }
    });

    return this.initPromise;
  }

  cancelExecution = () => {
    trackEvent('[PythonWebWorker].restartFromUser');
    this.initWorker();
    quadraticCore.sendCancelExecution('Python');
  };

  restart = () => {
    this.initWorker();
  };
}

export const pythonWebWorker = new PythonWebWorker();
